// Task action semantics. Mirrors internal/kinds/task/task.go Apply exactly:
// same transitions, same error classes, same timestamp/rounding rules.
import { RRule } from 'rrule'
import type { Edge, Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { dateOf, formatTime, isDate, minutesBetween, parseDate, todayLocal } from '@/lib/time'
import { isOpen, isTemplate, type TaskSpec, type TaskStatus } from './schema'

export type TaskResource = Resource<TaskSpec, TaskStatus>

export const defaults = (spec: Partial<TaskSpec>): TaskSpec => {
  const out: TaskSpec = { title: '', remind: false, priority: 0, ...spec }
  if (out.checklist) out.checklist = out.checklist.map((c) => ({ text: c.text, done: c.done ?? false }))
  return out
}

/** Parse an RRULE string ("FREQ=…" or "RRULE:FREQ=…", optional DTSTART line). */
export const parseRule = (recurrence: string, dtstart: Date): RRule => {
  const s = recurrence.trim()
  if (!s) throw invalid('recurrence is empty')
  try {
    const opts = RRule.parseString(s)
    if (!opts.dtstart) opts.dtstart = dtstart
    if (opts.freq === undefined) throw new Error('missing FREQ')
    return new RRule(opts)
  } catch (e) {
    throw invalid(`recurrence: ${(e as Error).message}`)
  }
}

const dayOfCreated = (r: TaskResource) => {
  const d = new Date(r.created_at)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export const dtstartOf = (r: TaskResource): Date =>
  r.spec.scheduled ? parseDate(r.spec.scheduled) : dayOfCreated(r)

const dateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** First occurrence on/after a local calendar day, as YYYY-MM-DD. */
export const nextOccurrence = (rule: RRule, day: Date): string | null => {
  const occ = rule.after(new Date(day.getTime() - 1000), false)
  return occ ? dateStr(occ) : null
}

export const validate = (r: TaskResource, edgesFrom: Edge[]): string[] => {
  const errs: string[] = []
  const s = r.spec
  if (s.scheduled && s.due && s.scheduled > s.due) errs.push('scheduled must be on or before due')
  if (s.every_days && s.recurrence) errs.push('every_days and recurrence are separate ways to repeat')
  if (s.scheduled_time && !s.scheduled && !s.recurrence) errs.push('scheduled_time requires scheduled')
  if (s.recurrence) {
    try {
      parseRule(s.recurrence, dtstartOf(r))
    } catch (e) {
      errs.push((e as Error).message)
    }
  }
  if (edgesFrom.filter((e) => e.relation === 'belongs_to' && !e.deleted_at).length > 1)
    errs.push('a task belongs to at most one project')
  return errs
}

export const initialStatus = (r: TaskResource): TaskStatus => {
  const st: TaskStatus = {
    state: r.spec.scheduled ? 'next' : 'inbox',
    actual_minutes: 0,
    reschedule_count: 0,
    last_touched: formatTime(new Date(r.created_at)),
  }
  if (isTemplate(r.spec)) {
    st.state = 'next'
    const rule = parseRule(r.spec.recurrence!, dtstartOf(r))
    const occ = nextOccurrence(rule, parseDate(todayLocal()))
    if (occ) st.next_due = occ
    else st.state = 'done'
  }
  return compact(st)
}

export const displayName = (r: TaskResource) => r.spec.title

export const afterPatch = (r: TaskResource, at: string): TaskResource => ({
  ...r,
  status: compact({ ...r.status, last_touched: formatTime(new Date(at)) }),
})

const stopTimer = (st: TaskStatus, at: string): TaskStatus => {
  if (!st.started_at) return st
  const start = new Date(st.started_at)
  let end = new Date(at)
  if (end < start) end = start
  const sessions = [...(st.sessions ?? []), { start: formatTime(start), end: formatTime(end) }]
  return {
    ...st,
    sessions,
    actual_minutes: st.actual_minutes + minutesBetween(formatTime(start), formatTime(end)),
    started_at: undefined,
  }
}

const asObj = (a: unknown): Record<string, unknown> =>
  a && typeof a === 'object' ? (a as Record<string, unknown>) : {}

export const apply = (r: TaskResource, action: string, args: unknown, at: string): TaskResource => {
  const when = formatTime(new Date(at))
  let st: TaskStatus = { ...r.status }
  let spec: TaskSpec = { ...r.spec }
  const clocks = { ...r.clocks }
  const a = asObj(args)
  switch (action) {
    case 'complete': {
      if (!isOpen(st.state)) throw conflict(`task is already ${st.state}`)
      st = stopTimer(st, at)
      st.state = 'done'
      st.completed_at = when
      if (spec.checklist && spec.checklist.length > 0)
        spec = { ...spec, checklist: spec.checklist.map((c) => ({ ...c, done: true })) }
      break
    }
    case 'drop': {
      if (!isOpen(st.state)) throw conflict(`task is already ${st.state}`)
      st = stopTimer(st, at)
      st.state = 'dropped'
      st.dropped_reason = typeof a.reason === 'string' ? a.reason : ''
      break
    }
    case 'convert': {
      if (!isOpen(st.state)) throw conflict(`task is already ${st.state}`)
      const ref = a as { kind?: unknown; id?: unknown }
      if (typeof ref.kind !== 'string' || !ref.kind || typeof ref.id !== 'string' || !ref.id)
        throw invalid('convert: kind and id required')
      st = stopTimer(st, at)
      st.state = 'dropped'
      st.dropped_reason = 'converted'
      st.converted_to = { kind: ref.kind, id: ref.id }
      break
    }
    case 'reopen': {
      if (isOpen(st.state)) throw conflict('task is not closed')
      st.state = 'next'
      st.completed_at = undefined
      st.dropped_reason = undefined
      break
    }
    case 'move': {
      const s = a.state
      if (s !== 'inbox' && s !== 'next' && s !== 'waiting' && s !== 'someday')
        throw invalid('move: state must be one of inbox|next|waiting|someday')
      if (!isOpen(st.state)) throw conflict(`task is ${st.state}; reopen it first`)
      st.state = s
      break
    }
    case 'defer': {
      const scheduled = a.scheduled
      if (typeof scheduled !== 'string' || !scheduled)
        throw invalid('defer: scheduled (YYYY-MM-DD) is required')
      if (!isDate(scheduled)) throw invalid('defer: scheduled must be YYYY-MM-DD')
      if (!isOpen(st.state)) throw conflict(`task is ${st.state}`)
      spec = { ...spec, scheduled }
      clocks['spec.scheduled'] = when
      if (a.scheduled_time !== undefined && a.scheduled_time !== null) {
        if (a.scheduled_time === '') delete spec.scheduled_time
        else spec.scheduled_time = String(a.scheduled_time)
        clocks['spec.scheduled_time'] = when
      }
      if (st.state === 'inbox') st.state = 'next'
      st.reschedule_count = st.reschedule_count + 1
      st.reminded_at = undefined
      break
    }
    case 'start': {
      if (!isOpen(st.state)) throw conflict(`task is ${st.state}`)
      if (st.started_at) throw conflict('task is already started')
      st.started_at = when
      break
    }
    case 'stop': {
      if (!st.started_at) throw conflict('task is not started')
      st = stopTimer(st, at)
      break
    }
    case 'touch':
      break
    case 'done_now': {
      if (!isOpen(st.state)) throw conflict(`task is already ${st.state}`)
      if (a.day !== undefined && a.day !== null) {
        // undo: put a day back ("" clears it); nothing else about the task moves
        if (typeof a.day !== 'string' || (a.day !== '' && !isDate(a.day)))
          throw invalid('done_now: day must be YYYY-MM-DD')
        st.done_at = a.day || undefined
        break
      }
      st.done_at = dateOf(new Date(at))
      // a job done now has no day waiting for it: it goes back where an undated task lives
      if (spec.scheduled !== undefined) {
        spec = { ...spec }
        delete spec.scheduled
        clocks['spec.scheduled'] = when
      }
      if (spec.scheduled_time !== undefined) {
        spec = { ...spec }
        delete spec.scheduled_time
        clocks['spec.scheduled_time'] = when
      }
      break
    }
    default:
      throw invalid(`unknown action ${action}`)
  }
  st.last_touched = when
  return { ...r, spec, status: compact(st), clocks }
}
